import React, { useCallback, useMemo } from 'react';

interface UsePagedSelectionOptions<T> {
    currentPageItems: T[];
    getId: (item: T) => number;
    initialSelected?: Record<number, boolean>;
}

interface UsePagedSelectionReturn<T> {
    selectedById: Record<number, boolean>;
    selectedCount: number;
    isSelected: (id: number) => boolean;
    toggleOne: (id: number) => void;
    toggleAllCurrentPage: () => void;
    isAllCurrentPageSelected: boolean;
    getSelectedIds: () => number[];
    clearSelection: () => void;
}

export function usePagedSelection<T>({
    currentPageItems,
    getId,
    initialSelected = {},
}: UsePagedSelectionOptions<T>): UsePagedSelectionReturn<T> {
    const [selectedById, setSelectedById] = React.useState<Record<number, boolean>>(initialSelected);

    const currentPageIds = useMemo(() => {
        return currentPageItems.map((item) => getId(item));
    }, [currentPageItems, getId]);

    const selectedCount = useMemo(() => {
        return Object.keys(selectedById).filter((id) => selectedById[id]).length;
    }, [selectedById]);

    const isSelected = useCallback(
        (id: number) => {
            return !!selectedById[id];
        },
        [selectedById]
    );

    const toggleOne = useCallback((id: number) => {
        setSelectedById((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    }, []);

    const toggleAllCurrentPage = useCallback(() => {
        setSelectedById((prev) => {
            const newSelected = { ...prev };
            const allSelected = currentPageIds.every((id) => prev[id]);

            if (allSelected) {
                currentPageIds.forEach((id) => {
                    delete newSelected[id];
                });
            } else {
                currentPageIds.forEach((id) => {
                    newSelected[id] = true;
                });
            }

            return newSelected;
        });
    }, [currentPageIds]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageItems.length > 0 && currentPageIds.every((id) => selectedById[id]);
    }, [currentPageItems.length, currentPageIds, selectedById]);

    const getSelectedIds = useCallback(() => {
        return Object.keys(selectedById)
            .filter((id) => selectedById[id])
            .map((id) => Number(id));
    }, [selectedById]);

    const clearSelection = useCallback(() => {
        setSelectedById({});
    }, []);

    return {
        selectedById,
        selectedCount,
        isSelected,
        toggleOne,
        toggleAllCurrentPage,
        isAllCurrentPageSelected,
        getSelectedIds,
        clearSelection,
    };
}
